import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, TextField, Select,
  MenuItem, FormControl, InputLabel, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Skeleton, Chip, IconButton, Collapse, Tooltip,
} from '@mui/material';
import { Search, FilterList, Clear, OpenInNew, ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { eventsAPI } from '../services/api';
import SeverityChip from '../components/common/SeverityChip';
import EventTypeChip from '../components/common/EventTypeChip';

const EVENT_TYPES  = ['SOLAR_FLARE', 'GEOMAGNETIC_STORM', 'CME', 'RADIATION_BELT', 'SOLAR_ENERGETIC_PARTICLE', 'INTERPLANETARY_SHOCK'];
const SEVERITIES   = ['LOW', 'MODERATE', 'HIGH', 'EXTREME', 'UNKNOWN'];

export default function EventsPage() {
  const navigate = useNavigate();

  const [events, setEvents]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({ type: '', severity: '', startDate: '', endDate: '' });
  const [page, setPage]       = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, size: rowsPerPage,
        sort: 'eventDate,desc',
        ...(filters.type      && { type: filters.type }),
        ...(filters.severity  && { severity: filters.severity }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate   && { endDate: filters.endDate }),
      };
      const res = await eventsAPI.getEvents(params);
      const data = res.data.data;
      setEvents(data.content);
      setTotal(data.totalElements);
    } catch (err) {
      console.error('Events load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const clearFilters = () => {
    setFilters({ type: '', severity: '', startDate: '', endDate: '' });
    setPage(0);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await eventsAPI.refresh();
      await loadEvents();
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Space Events</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {total.toLocaleString()} events in database
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Pull latest events from NASA DONKI">
            <Button
              variant="outlined" size="small"
              startIcon={<Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'text.secondary' }}
            >
              {refreshing ? 'Fetching…' : 'Refresh'}
            </Button>
          </Tooltip>
          <Button
            variant="outlined" size="small"
            startIcon={<FilterList />}
            endIcon={activeFiltersCount > 0 ? <Chip label={activeFiltersCount} size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'primary.main', color: 'background.default', ml: -0.5 }} /> : null}
            onClick={() => setShowFilters(v => !v)}
            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'primary.light' }}
          >
            Filters {showFilters ? <ExpandLess sx={{ ml: 0.5, fontSize: 16 }} /> : <ExpandMore sx={{ ml: 0.5, fontSize: 16 }} />}
          </Button>
        </Box>
      </Box>

      {/* Filters panel */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Event Type</InputLabel>
                  <Select value={filters.type} label="Event Type" onChange={e => { setFilters(f => ({ ...f, type: e.target.value })); setPage(0); }}>
                    <MenuItem value=""><em>All Types</em></MenuItem>
                    {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Severity</InputLabel>
                  <Select value={filters.severity} label="Severity" onChange={e => { setFilters(f => ({ ...f, severity: e.target.value })); setPage(0); }}>
                    <MenuItem value=""><em>All Severities</em></MenuItem>
                    {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField fullWidth size="small" label="Start Date" type="date" value={filters.startDate}
                  onChange={e => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(0); }}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField fullWidth size="small" label="End Date" type="date" value={filters.endDate}
                  onChange={e => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(0); }}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button fullWidth variant="text" startIcon={<Clear />} onClick={clearFilters}
                  disabled={activeFiltersCount === 0}
                  sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Active filter chips */}
      {activeFiltersCount > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {filters.type      && <Chip size="small" label={`Type: ${filters.type.replace(/_/g,' ')}`} onDelete={() => setFilters(f => ({ ...f, type: '' }))} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', borderColor: 'rgba(255,255,255,0.3)' }} variant="outlined" />}
          {filters.severity  && <Chip size="small" label={`Severity: ${filters.severity}`} onDelete={() => setFilters(f => ({ ...f, severity: '' }))} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', borderColor: 'rgba(255,255,255,0.3)' }} variant="outlined" />}
          {filters.startDate && <Chip size="small" label={`From: ${filters.startDate}`} onDelete={() => setFilters(f => ({ ...f, startDate: '' }))} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', borderColor: 'rgba(255,255,255,0.3)' }} variant="outlined" />}
          {filters.endDate   && <Chip size="small" label={`To: ${filters.endDate}`} onDelete={() => setFilters(f => ({ ...f, endDate: '' }))} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', borderColor: 'rgba(255,255,255,0.3)' }} variant="outlined" />}
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {['Type', 'Date (UTC)', 'Severity', 'Source / Speed / Kp', 'Class', 'Notes', ''].map(h => (
                <TableCell key={h} sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array(rowsPerPage).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    {Array(7).fill(0).map((_, j) => (
                      <TableCell key={j}><Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : events.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <Search sx={{ fontSize: 40, opacity: 0.3, mb: 1, display: 'block', mx: 'auto' }} />
                      No events found matching your filters
                    </TableCell>
                  </TableRow>
                )
                : events.map(event => (
                  <TableRow key={event.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }, cursor: 'pointer' }}
                    onClick={() => navigate(`/events/${event.id}`)}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 1.4 }}>
                      <EventTypeChip type={event.type} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {format(new Date(event.eventDate), 'MMM dd, yyyy')}<br />
                      <span style={{ fontSize: 11 }}>{format(new Date(event.eventDate), 'HH:mm')} UTC</span>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <SeverityChip severity={event.severity} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12 }}>
                      {event.sourceLocation && <Box sx={{ color: 'primary.light', fontWeight: 600 }}>{event.sourceLocation}</Box>}
                      {event.speedKmS != null && <Box sx={{ color: '#26c6da' }}>{Math.round(event.speedKmS)} km/s</Box>}
                      {event.kpIndex  != null && <Box sx={{ color: '#7e57c2' }}>Kp {event.kpIndex?.toFixed(1)}</Box>}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#ffa726', fontWeight: 700, fontSize: 13 }}>
                      {event.flareClass || '—'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12, maxWidth: 200 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.note || event.instruments || '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <OpenInNew sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: 'text.secondary' }}
        />
      </TableContainer>
    </Box>
  );
}
